import { QuestionCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import React from "react";
import styles from "./question-input.module.css";
import { CustomTooltip } from "src/components/CustomTooltip";

// Main clause tooltips
const tooltipContent = {
  select: {
    title: "Select Entity",
    description:
      "Start by choosing the data type you want to analyze. This defines the foundation of your analysis and determines which fields are available.",
  },
  measureBy: {
    title: "Measure",
    description:
      "Define what you want to calculate or analyze. This will become your key metrics, like # of issues completed or commits submitted.",
  },
  groupBy: {
    title: "Group By",
    description:
      "Segment your data to discover patterns and trends. For example, group by team to view activity across teams.",
  },
  having: {
    title: "Having",
    description:
      "Focus on specific segments that meet certain criteria. For instance, show only PRs with > 5 comments to identify PRs that are discussed.",
  },
  formula: {
    title: "Formula",
    description:
      "Create custom metrics by combining existing measures. Perfect for calculating % of issues completed, ratios, or Delta between two measures.",
  },
  searchTerm: {
    title: "Search",
    description:
      "Quickly filter your data using simple keywords and operators. Useful for focusing on specific segments or categories.",
  },
  compareWith: {
    title: "Compare With",
    description:
      "Add context to your analysis by comparing with different time periods or segments. Great for tracking growth and trends.",
  },
  dateRange: {
    title: "Date Range",
    description:
      "Focus your analysis on a specific time period. Essential for tracking performance over time or analyzing seasonal patterns.",
  },
};

// Field-specific tooltips with enhanced explanations
export const fieldTooltips = {
  // Entity and Field Selection
  entity:
    "The foundation of your analysis. Choose this carefully as it determines which metrics and groupings are available to you.",
  field:
    "Select specific attributes to analyze. These fields contain the raw data you will use to build insights.",

  // Measure Related
  measure:
    "Pick the data point you want to measure. This could be numerical values like count of issues completed , or avg. time in meetings per day",
  aggregation:
    "Choose how to analyze your data. SUM for totals, COUNT for occurrences, Percentiles for distribution",

  // Grouping and Categories
  groupByField:
    "Choose how to segment your data. Good groupings reveal patterns - like Cycle Time by Repository",

  // Filtering
  filterField:
    "Narrow down your data to focus on what matters. Use filters to remove noise and focus on relevant segments.",
  filterOperator:
    'Define how strictly to filter. "Equals" for exact matches, "Contains" for partial matches, "Between" for ranges.',
  filterValue:
    "Set your threshold or criteria. Be specific to get precisely the data you need.",

  // Formula and Calculations
  formula:
    "Build custom calculations combining different measures. Use this to create complex metrics like profit margins or custom scores.",

  // Time and Comparison
  dateRange:
    "Set your time window. Recent data for current performance, longer ranges for trends and patterns.",
  compareType:
    "Choose your comparison basis. Year-over-year for annual patterns, previous period for immediate trends.",

  // Search and Having
  searchTerm:
    "Quick way to find specific data. Use operators like AND, OR to build complex filters without multiple steps.",
  havingField:
    "Select which calculated measure to filter on. Useful for finding top performers or identifying outliers.",
  havingOperator:
    "Choose how to compare your measure. Greater than for top performers, less than for underperformers.",
  havingValue:
    "Set your threshold for filtering groups. Make it meaningful for your analysis - like >5 days for average time to merge.",

  // Measure Filters
  measureFilterField:
    "Refine how your measure is calculated by filtering the underlying data. Useful for focused metrics.",
  measureFilterOperator:
    "Define the comparison logic for your measure filter. Helps create precise measure calculations.",
  measureFilterValue:
    "Set specific criteria for your measure calculation. Ensures your metrics reflect exactly what you want to analyze.",
};

export function ClauseTooltip({
  type,
  children,
}: {
  type: keyof typeof tooltipContent;
  children: React.ReactNode;
}) {
  const content = tooltipContent[type];

  return (
    <div className={styles.labelWrapper}>
      {children}
      <CustomTooltip
        content={
          <div>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>
              {content.title}
            </div>
            <div>{content.description}</div>
          </div>
        }
      >
        <QuestionCircleOutlined className={styles.tooltipIcon} />
      </CustomTooltip>
    </div>
  );
}

export function FieldTooltip({
  type,
  children,
}: {
  type: keyof typeof fieldTooltips;
  children: React.ReactNode;
}) {
  return (
    <div className={styles.labelWrapper}>
      {children}
      <CustomTooltip content={fieldTooltips[type]}>
        <QuestionCircleOutlined className={styles.tooltipIcon} />
      </CustomTooltip>
    </div>
  );
}
